import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Paperclip, X } from "lucide-react";
import Reveal from "@/components/Reveal";
import { WhatsAppIcon } from "@/components/Logo";
import { EMAIL, PHONE_DISPLAY, PHONE_TEL, WHATSAPP_LINK } from "@/site";
import { IMGS } from "@/data";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldClass =
  "w-full border-0 border-b border-ink/20 bg-transparent px-0 py-3 text-base text-ink placeholder:text-ink/35 outline-none transition-colors duration-300 focus:border-clay focus:ring-0";

const ALLOWED = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
const MAX_BYTES = 10 * 1024 * 1024;

const QuoteForm = () => {
  const { t } = useLang();
  const empty = {
    name: "", business: "", email: "", phone: "",
    garment_type: t.quote.types[0], quantity: "", timeline: "", message: "",
  };
  const [form, setForm] = useState(empty);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const addFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    const next = [...files];
    for (const f of chosen) {
      if (next.length >= 5) break;
      if (!ALLOWED.includes(f.type)) {
        toast.error(`${f.name}: ${t.quote.fileBadType}`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: ${t.quote.fileTooBig}`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles((fs) => fs.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/enquiries`, fd);
      toast.success(t.quote.success);
      setForm({ ...empty, garment_type: form.garment_type });
      setFiles([]);
    } catch (err) {
      toast.error(t.quote.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="border-t border-ink/10 bg-white py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 md:px-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-clay">{t.quote.overline}</p>
          <h2 data-testid="quote-heading" className="mt-6 font-serif text-4xl tracking-tight text-ink sm:text-5xl">
            {t.quote.h2a} <em className="italic text-clay">{t.quote.h2b}</em>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-ink/60">{t.quote.sub}</p>
          <p data-testid="quote-note" className="mt-6 max-w-md border-s-2 border-clay ps-4 font-serif text-base italic leading-relaxed text-ink/75">
            {t.quote.note}
          </p>
          <div className="mt-10 space-y-4 text-sm text-ink/70">
            <p data-testid="quote-contact-email">
              <span className="me-3 text-xs uppercase tracking-[0.18em] text-ink/40">{t.quote.emailL}</span>
              {EMAIL}
            </p>
            <p data-testid="quote-contact-phone">
              <span className="me-3 text-xs uppercase tracking-[0.18em] text-ink/40">{t.quote.phoneL}</span>
              <a href={PHONE_TEL} className="transition-colors duration-300 hover:text-clay">{PHONE_DISPLAY}</a>
            </p>
          </div>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="quote-whatsapp-button"
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-ink/20 px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-bone"
          >
            <WhatsAppIcon className="h-4 w-4" />
            {t.quote.chatCta}
          </a>
          <div className="mt-12 hidden max-w-xs overflow-hidden border-4 border-bone shadow-[0_16px_40px_rgba(0,0,0,0.10)] lg:block">
            <img
              src="/images/site-qc-check.webp"
              alt="HHB Clothing Co quality controller checking a production list on the factory floor"
              data-testid="quote-side-image"
              loading="lazy"
              className="aspect-[3/4] w-full object-cover"
            />
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form data-testid="quote-form" onSubmit={submit} className="grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2">
            <div>
              <label htmlFor="q-name" className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">{t.quote.labels.name}</label>
              <input id="q-name" data-testid="quote-input-name" required value={form.name} onChange={set("name")} placeholder={t.quote.placeholders.name} className={fieldClass} />
            </div>
            <div>
              <label htmlFor="q-business" className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">{t.quote.labels.business}</label>
              <input id="q-business" data-testid="quote-input-business" value={form.business} onChange={set("business")} placeholder={t.quote.placeholders.business} className={fieldClass} />
            </div>
            <div>
              <label htmlFor="q-email" className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">{t.quote.labels.email}</label>
              <input id="q-email" type="email" data-testid="quote-input-email" required value={form.email} onChange={set("email")} placeholder={t.quote.placeholders.email} className={fieldClass} />
            </div>
            <div>
              <label htmlFor="q-phone" className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">{t.quote.labels.phone}</label>
              <input id="q-phone" data-testid="quote-input-phone" required value={form.phone} onChange={set("phone")} placeholder={t.quote.placeholders.phone} className={fieldClass} />
            </div>
            <div>
              <label htmlFor="q-type" className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">{t.quote.labels.type}</label>
              <select id="q-type" data-testid="quote-select-garment" value={form.garment_type} onChange={set("garment_type")} className={`${fieldClass} cursor-pointer`}>
                {t.quote.types.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="q-qty" className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">{t.quote.labels.qty}</label>
              <input id="q-qty" data-testid="quote-input-quantity" required value={form.quantity} onChange={set("quantity")} placeholder={t.quote.placeholders.qty} className={fieldClass} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="q-timeline" className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">{t.quote.labels.timeline}</label>
              <input id="q-timeline" data-testid="quote-input-timeline" value={form.timeline} onChange={set("timeline")} placeholder={t.quote.placeholders.timeline} className={fieldClass} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="q-message" className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">{t.quote.labels.message}</label>
              <textarea id="q-message" data-testid="quote-input-message" rows={3} value={form.message} onChange={set("message")} placeholder={t.quote.placeholders.message} className={`${fieldClass} resize-none`} />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">{t.quote.files}</label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.webp,.pdf"
                onChange={addFiles}
                data-testid="quote-input-files"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="quote-attach-button"
                className="mt-3 inline-flex items-center gap-2 rounded-full border border-dashed border-ink/30 px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-ink/70 transition-colors duration-300 hover:border-clay hover:text-clay"
              >
                <Paperclip className="h-4 w-4" />
                {t.quote.files}
              </button>
              <p className="mt-2 text-xs text-ink/40">{t.quote.filesHint}</p>
              {files.length > 0 && (
                <ul data-testid="quote-file-list" className="mt-4 flex flex-wrap gap-2">
                  {files.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="inline-flex items-center gap-2 rounded-full border border-clay/40 bg-bone px-4 py-2 text-xs font-semibold text-ink">
                      <Paperclip className="h-3.5 w-3.5 text-clay" />
                      {f.name}
                      <button type="button" onClick={() => removeFile(i)} data-testid={`quote-file-remove-${i}`} aria-label={`Remove ${f.name}`} className="text-ink/40 transition-colors duration-300 hover:text-red-700">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={sending}
                data-testid="quote-submit-button"
                className="group inline-flex items-center gap-3 rounded-full bg-ink px-10 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-bone transition-colors duration-300 hover:bg-clay disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? t.quote.sending : t.quote.submit}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180" />
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default QuoteForm;
